// @ts-strict-ignore
import { FieldBlock, InputBlock, TextStyle } from '@coast/schemas';
import { ReactElement, useCallback, useContext } from 'react';
import { View } from '@shared/components/View';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBClearButton } from '@shared/components/button';
import { Switch } from '@shared/components/form/switch';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { useLayoutConfig } from '../../contexts/layoutConfig';

export interface FormInputProps {
  block: InputBlock;
}

export function getTextInputSize(textStyle: TextStyle): 'normal' | 'large' | 'xlarge' {
  switch (textStyle) {
    case 'h3':
      return 'xlarge';
    case 'h4':
      return 'large';
    case 'body1':
    case 'body1bold':
    default:
      return 'normal';
  }
}

const BOLD_TYPES: string[] = [
  'body1bold',
  'body2bold',
  'overlinebold',
  'caption1bold',
  'caption2bold',
];
export function isInputBold(textStyle: TextStyle): boolean {
  return BOLD_TYPES.includes(textStyle);
}

function BlockIcon({ name, icon }: { name?: string; icon: FieldBlock['icon'] }) {
  const colors = useContext(ColorThemeContext);
  const cardTheme = useContext(CardThemeContext);
  const { errors } = useContext(CardFormContext);
  const {
    blockIcon: { size },
  } = useLayoutConfig();
  const error = errors[name];
  return (
    <MBIconV2
      color={(() => {
        if (!!error) {
          return colors.error.foreground;
        }
        if (cardTheme === 'stale') {
          return colors.foregroundInactive;
        }
        return colors.accent;
      })()}
      name={icon}
      size={size}
    />
  );
}

export function renderIcon(icon: FieldBlock['icon'], name?: string): ReactElement {
  return !!icon && <BlockIcon name={name} icon={icon} />;
}

function Label({ block }: FormInputProps) {
  const { values } = useContext(CardFormContext);
  const value = values[block.name];
  if (!block.label) {
    return null;
  }
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return <MBBlockLabel label={block.label} />;
}

export function renderLabel(block: InputBlock): ReactElement {
  return <Label block={block} />;
}

function ClearButton({ block, onClear }: FormInputProps & { onClear?: () => void }) {
  const { values, setFieldValue } = useContext(CardFormContext);
  const value = values[block.name];
  const onChange = useCallback(() => {
    setFieldValue(block.name, null);
    onClear?.();
  }, [block.name, setFieldValue, onClear]);
  if (value) {
    return <MBClearButton onPress={onChange} />;
  }
  return null;
}
export function renderClearButton(block: InputBlock, onClear?: () => void): ReactElement {
  return <ClearButton block={block} onClear={onClear} />;
}

function Error({ block }: FormInputProps) {
  const colors = useContext(ColorThemeContext);
  const { errors } = useContext(CardFormContext);
  const error = errors[block.name];
  if (Boolean(error)) {
    return (
      <View paddingTop={0} padding={2}>
        <MBTextBody2 color={colors.error.foreground}>{error}</MBTextBody2>
      </View>
    );
  }
  return null;
}

export function renderError(block: InputBlock): ReactElement {
  return <Error block={block} />;
}

export function renderToggle(value: boolean, onChange: (value: boolean) => void) {
  return <Switch value={value} onValueChange={onChange} />;
}
